import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, TrendingUp, Briefcase, Award, ArrowRight, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { API } from '../App';
import { analytics } from '../lib/analytics';

const DEMAND_CONFIG = {
  'Very High': { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Very High Demand' },
  'High': { color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', label: 'High Demand' },
  'Medium': { color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Medium Demand' },
  'Low': { color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'Low Demand' },
};

function SalaryCard({ role }) {
  const demand = DEMAND_CONFIG[role.demand] || DEMAND_CONFIG['Medium'];
  const midSalary = Math.round((role.salary_min + role.salary_max) / 2);

  return (
    <div data-testid={`salary-card-${role.title.replace(/\s+/g, '-').toLowerCase()}`} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md hover:border-gray-300 transition-all duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900 text-base">{role.title}</h3>
          <span className="text-xs text-gray-500">{role.category}</span>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${demand.bg} ${demand.color} ${demand.border}`}>
          {demand.label}
        </span>
      </div>

      {/* Salary Range */}
      <div className="mb-4">
        <div className="flex items-baseline gap-1 mb-1">
          <span className="text-2xl font-bold text-gray-900">{role.salary_min.toLocaleString()}</span>
          <span className="text-gray-400 text-sm">–</span>
          <span className="text-2xl font-bold text-gray-900">{role.salary_max.toLocaleString()}</span>
          <span className="text-sm text-gray-500 ml-1">AED/mo</span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#CE1126] to-[#009639]"
            style={{ width: `${Math.min(100, (midSalary / 25000) * 100)}%` }}
          />
        </div>
      </div>

      {/* Experience */}
      <div className="flex items-center gap-2 mb-3 text-sm">
        <Briefcase className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-gray-600">Experience: <span className="font-medium text-gray-800">{role.experience}</span></span>
      </div>

      {/* Skills */}
      <div>
        <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Required Skills</p>
        <div className="flex flex-wrap gap-1.5">
          {role.skills.map((skill, i) => (
            <span key={i} className="text-xs px-2 py-1 bg-gray-50 text-gray-700 rounded border border-gray-150">
              {skill}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SalaryInsightPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    axios.get(`${API}/salary/popular`).then(res => {
      setPopular(res.data.roles || []);
    }).catch(() => {});
  }, []);

  const handleSearch = async (searchQuery) => {
    const q = searchQuery || query;
    if (!q || q.trim().length < 2) return;

    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(`${API}/salary/lookup`, { params: { job_title: q.trim() } });
      setResults(res.data.results || []);
      setMessage(res.data.message || '');
      analytics.salarySearch(q.trim());
    } catch {
      setResults([]);
      setMessage('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const quickSearches = ['Sales', 'Admin', 'Customer Service', 'IT', 'Marketing', 'Accountant', 'HR', 'Nurse'];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="bg-white border-b">
        <div className="container mx-auto max-w-4xl px-4 py-12 md:py-16 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-sm font-medium px-3 py-1 rounded-full mb-4 border border-emerald-200">
            <TrendingUp className="w-3.5 h-3.5" />
            UAE Job Market 2026
          </div>
          <h1 data-testid="salary-page-title" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 tracking-tight">
            UAE Salary Insights
          </h1>
          <p className="text-gray-500 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
            Know your worth in the UAE job market. Search any role to see salary ranges, required skills, and experience levels.
          </p>

          {/* Search Bar */}
          <div data-testid="salary-search-bar" className="relative max-w-xl mx-auto mb-5">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              data-testid="salary-search-input"
              type="text"
              placeholder="Search job title... e.g. Sales Executive, Accountant"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full pl-12 pr-28 py-3.5 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-[#CE1126]/30 focus:border-[#CE1126] transition-all"
            />
            <Button
              data-testid="salary-search-button"
              onClick={() => handleSearch()}
              disabled={loading || query.trim().length < 2}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 btn-uae-red px-5"
              size="sm"
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>

          {/* Quick Search Tags */}
          <div className="flex flex-wrap justify-center gap-2">
            {quickSearches.map((tag) => (
              <button
                key={tag}
                data-testid={`quick-search-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => { setQuery(tag); handleSearch(tag); }}
                className="text-xs px-3 py-1.5 rounded-full border border-gray-200 text-gray-600 hover:border-[#CE1126] hover:text-[#CE1126] hover:bg-red-50 transition-all"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="flex-1 container mx-auto max-w-5xl px-4 py-8">
        {searched && (
          <div className="mb-6">
            <p className="text-sm text-gray-500">{message}</p>
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#CE1126]"></div>
          </div>
        )}

        {!loading && searched && results.length > 0 && (
          <div data-testid="salary-results-grid" className="grid md:grid-cols-2 gap-4 mb-10">
            {results.map((role, i) => (
              <SalaryCard key={i} role={role} />
            ))}
          </div>
        )}

        {!loading && searched && results.length === 0 && (
          <div data-testid="salary-no-results" className="text-center py-12">
            <p className="text-gray-500 mb-2">No exact matches found.</p>
            <p className="text-sm text-gray-400">Try broader keywords like "Sales", "IT", or "Marketing".</p>
          </div>
        )}

        {/* CTA Section */}
        <div data-testid="salary-cta-section" className="bg-white border border-gray-200 rounded-lg p-6 md:p-8 text-center mt-4">
          <div className="flex justify-center mb-3">
            <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
              <Award className="w-5 h-5 text-[#CE1126]" />
            </div>
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
            Is your CV ready for these salaries?
          </h2>
          <p className="text-gray-500 text-sm mb-5 max-w-md mx-auto">
            Check if your CV passes ATS filters used by top UAE employers. Free instant score.
          </p>
          <Link to="/cv-checker">
            <Button data-testid="salary-cta-button" className="btn-uae-red px-6">
              Check Your CV Readiness Now <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Popular Roles - shown when no search yet */}
        {!searched && popular.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#CE1126]" />
              High Demand Roles in UAE
            </h2>
            <div data-testid="popular-roles-grid" className="grid md:grid-cols-2 gap-4">
              {popular.map((role, i) => (
                <SalaryCard key={i} role={role} />
              ))}
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
